import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ApiError, api, readTableFile } from '../api/client.js'
import type { Crumb, Issue, ObjectDetail, ObjectImportResult, ObjectKind, ObjectNode, Plan, ResidentFeedback } from '../api/types.js'
import { ColumnMapper } from '../components/ColumnMapper.js'
import { Icon, type IconName } from '../components/icons.js'
import { KIND_LABEL, KIND_PLURAL, KindBadge, ObjectPicker, childKinds, formatAddress } from '../components/objects.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Field, Modal, Notice, Spinner, StatusBadge, Tile } from '../components/ui.js'
import { addDays, formatDate, formatDateTime, todayIso } from '../lib/format.js'
import { PageHead } from './Layout.js'
import { PlanFormModal, draftFromPlan, emptyPlanDraft, spreadLabel, type PlanDraft } from './PlanForm.js'
import { AdhocModal, TaskModal } from './TaskDialogs.js'
import { CustomerAccess } from './CustomerAccess.js'
import { DocumentsTab } from './Documents.js'
import { History } from '../components/History.js'
import { ListImportModal, TABLE_ACCEPT } from '../components/ListImport.js'

/**
 * The object explorer: one tree for all four levels.
 *
 * Left the tree, opened branch by branch (three thousand Liegenschaften are
 * never loaded at once); right the selected node with everything that hangs
 * on it. The search box at the top jumps straight to a flat, a house number
 * or a Youtilly ID, that is how people find things, not by clicking down
 * from the customer.
 */
export function ObjectsPage() {
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [showImport, setShowImport] = useState(false)
  const [showRefs, setShowRefs] = useState(false)
  const [newChild, setNewChild] = useState<{ parent: ObjectNode | null } | null>(null)
  const [treeOpen, setTreeOpen] = useState(true)
  const [showArchived, setShowArchived] = useState(false)
  const isNarrow = () => window.matchMedia('(max-width: 900px)').matches
  // On a phone the tree and the detail cannot sit side by side: picking a
  // node hides the tree, the button above the detail brings it back.
  const pick = (node: ObjectNode) => {
    navigate(`/objekte/${node.id}`)
    if (isNarrow()) setTreeOpen(false)
  }

  const detail = useQuery({
    queryKey: ['object', id],
    queryFn: () => api<ObjectDetail>(`/api/objects/${id}`),
    enabled: Boolean(id),
  })

  return (
    <>
      <PageHead
        title="Objekte"
        subtitle="Kunde › Liegenschaft › Gebäude › Einheit. Aufgaben hängen an jeder Ebene ab der Liegenschaft."
        action={
          <div className="actions">
            <a className="btn secondary" href={`/api/lists/objects.xlsx${id ? `?objectId=${id}` : ''}`} title={id ? 'Dieses Objekt und alles darunter' : 'Alle Objekte'}>
              <Icon name="download" /> Als Excel
            </a>
            <button type="button" className="btn secondary" onClick={() => setShowImport(true)}>
              <Icon name="upload" /> Aus Excel importieren
            </button>
            <button type="button" className="btn secondary" onClick={() => setShowRefs(true)}>
              Youtilly-Positionen
            </button>
            <button type="button" className="btn" onClick={() => setNewChild({ parent: null })}>
              <Icon name="plus" /> Neuer Kunde
            </button>
          </div>
        }
      />

      <div className="explorer">
        <aside className={`card tree ${treeOpen ? '' : 'collapsed'}`}>
          <div className="tree-search">
            <input
              type="search"
              placeholder="Suchen: Name, Adresse, Nummer, Youtilly-ID"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Objekte durchsuchen"
            />
          </div>
          <div className="card-body">
            {search.trim() ? (
              <SearchResults term={search.trim()} selectedId={id} onPick={pick} />
            ) : (
              <Tree selectedId={id} ancestors={detail.data?.breadcrumb ?? []} onPick={pick} archived={showArchived} />
            )}
          </div>
          <label className="tree-archive small">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Archivierte anzeigen
          </label>
          <div className="tree-legend muted small">
            Zahl am Rand: offene Aufgaben hier und darunter, <span style={{ color: 'var(--red-700)', fontWeight: 700 }}>rot</span>: davon nicht
            erledigt.
          </div>
        </aside>

        <div>
          <button type="button" className="btn secondary small tree-mobile-toggle" onClick={() => setTreeOpen((v) => !v)}>
            <Icon name="tree" size={16} /> {treeOpen ? 'Baum ausblenden' : 'Baum einblenden'}
          </button>
          {!id ? (
            <Overview onNewCustomer={() => setNewChild({ parent: null })} />
          ) : detail.isLoading ? (
            <Spinner />
          ) : !detail.data ? (
            <Notice kind="error">Objekt nicht gefunden.</Notice>
          ) : (
            <NodeDetail node={detail.data} onNewChild={() => setNewChild({ parent: detail.data! })} />
          )}
        </div>
      </div>

      <ObjectFormModal
        open={newChild !== null}
        parent={newChild?.parent ?? null}
        onClose={() => setNewChild(null)}
        onSaved={(created) => {
          setNewChild(null)
          navigate(`/objekte/${created.id}`)
        }}
      />
      <ImportModal open={showImport} onClose={() => setShowImport(false)} />
      <ListImportModal
        open={showRefs}
        onClose={() => setShowRefs(false)}
        title="Youtilly-Positionen per Excel"
        endpoint="/api/service-refs/import"
        nouns={['Position', 'Positionen']}
        help={
          <>
            <p style={{ marginTop: 0 }}>
              Pro Gebäude (oder Einheit) und Leistung die Positions-ID aus Youtilly. Aufgaben übernehmen sie und melden sie zurück. Eine
              Aufgabe an der Liegenschaft meldet alle IDs der Gebäude darunter auf einmal.
            </p>
            <p>
              <a href={`/api/lists/service-refs.xlsx${id ? `?objectId=${id}` : ''}`}>
                <Icon name="download" size={14} /> Liste herunterladen
              </a>
              : alle Objekte und Leistungen aus den Plänen, mit den IDs, die schon da sind. Fehlende IDs in der letzten Spalte eintragen und hier
              wieder hochladen.
            </p>
          </>
        }
      />
    </>
  )
}

// ---------------------------------------------------------------- Baum

function useChildren(parentId: string | null, enabled: boolean, archived = false) {
  const extra = archived ? '&includeArchived=true' : ''
  return useQuery({
    queryKey: ['objects', parentId ?? 'root', archived ? 'mit-archiv' : 'aktiv'],
    queryFn: () => api<ObjectNode[]>(parentId ? `/api/objects?parentId=${parentId}${extra}` : `/api/objects?root=true${extra}`),
    enabled,
    staleTime: 30_000,
  })
}

export function Tree({
  selectedId,
  ancestors,
  onPick,
  archived = false,
}: {
  selectedId?: string
  ancestors: Crumb[]
  onPick: (node: ObjectNode) => void
  /** Also list archived objects, for looking up old proof. */
  archived?: boolean
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  // Opening a node from a link or a search opens the branch above it too.
  useEffect(() => {
    if (ancestors.length === 0) return
    setExpanded((current) => {
      const next = new Set(current)
      for (const crumb of ancestors) next.add(crumb.id)
      return next
    })
  }, [ancestors])

  const roots = useChildren(null, true, archived)
  if (roots.isLoading) return <Spinner />
  if (!roots.data || roots.data.length === 0) {
    return <EmptyState title="Noch keine Objekte" hint="Legen Sie einen Kunden an oder importieren Sie eine Liste." />
  }

  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <ul>
      {roots.data.map((node) => (
        <TreeNode key={node.id} node={node} expanded={expanded} selectedId={selectedId} onToggle={toggle} onPick={onPick} archived={archived} />
      ))}
    </ul>
  )
}

function TreeNode({
  node,
  expanded,
  selectedId,
  onToggle,
  onPick,
  archived = false,
}: {
  node: ObjectNode
  expanded: Set<string>
  selectedId?: string
  onToggle: (id: string) => void
  onPick: (node: ObjectNode) => void
  archived?: boolean
}) {
  const isOpen = expanded.has(node.id)
  // Archived children are not counted, so with the archive shown every node may open.
  const hasChildren = (node.descendants ?? 0) > 0 || (archived && node.kind !== 'unit')
  const children = useChildren(node.id, isOpen && hasChildren, archived)
  const missed = node.missedTasks ?? 0
  const open = node.openTasks ?? 0

  return (
    <li>
      <div
        className={`tree-row ${node.id === selectedId ? 'selected' : ''}`}
        onClick={() => {
          onPick(node)
          if (hasChildren && !isOpen) onToggle(node.id)
        }}
        role="treeitem"
        aria-expanded={hasChildren ? isOpen : undefined}
        aria-selected={node.id === selectedId}
      >
        <button
          type="button"
          className={`tree-toggle ${hasChildren ? '' : 'leaf'}`}
          aria-label={isOpen ? 'Zuklappen' : 'Aufklappen'}
          onClick={(event) => {
            event.stopPropagation()
            onToggle(node.id)
          }}
        >
          {isOpen ? '▼' : '▶'}
        </button>
        <KindBadge kind={node.kind} />
        <span className={`name ${node.archivedAt ? 'muted' : ''}`} title={node.archivedAt ? `${node.name} (archiviert)` : node.name}>
          {node.name}
          {node.archivedAt ? ', archiviert' : ''}
        </span>
        {missed > 0 ? (
          <span className="n bad" title={`${missed} nicht erledigt`}>
            {missed}
          </span>
        ) : open > 0 ? (
          <span className="n muted" title={`${open} offen`}>
            {open}
          </span>
        ) : null}
      </div>
      {isOpen && hasChildren && (
        <ul>
          {children.isLoading && <li className="muted small" style={{ padding: '4px 8px' }}>…</li>}
          {children.data?.map((child) => (
            <TreeNode key={child.id} node={child} expanded={expanded} selectedId={selectedId} onToggle={onToggle} onPick={onPick} archived={archived} />
          ))}
        </ul>
      )}
    </li>
  )
}

export function SearchResults({ term, selectedId, onPick }: { term: string; selectedId?: string; onPick: (node: ObjectNode) => void }) {
  const results = useQuery({
    queryKey: ['object-search', term],
    queryFn: () => api<ObjectNode[]>(`/api/objects?q=${encodeURIComponent(term)}&limit=60`),
    placeholderData: (previous) => previous,
  })
  if (results.isLoading) return <Spinner />
  if (!results.data || results.data.length === 0) return <div className="muted small" style={{ padding: 8 }}>Nichts gefunden.</div>
  return (
    <ul>
      {results.data.map((node) => (
        <li key={node.id}>
          <div className={`tree-row ${node.id === selectedId ? 'selected' : ''}`} onClick={() => onPick(node)}>
            <KindBadge kind={node.kind} />
            <span className="name">
              {node.name}
              <span className="muted small" style={{ display: 'block' }}>
                {(node.breadcrumb ?? []).map((c) => c.name).join(' › ') || formatAddress(node)}
              </span>
            </span>
          </div>
        </li>
      ))}
    </ul>
  )
}

// ---------------------------------------------------------------- Übersicht

function Overview({ onNewCustomer }: { onNewCustomer: () => void }) {
  const customers = useChildren(null, true)
  const sites = useQuery({ queryKey: ['objects', 'kind-site'], queryFn: () => api<ObjectNode[]>('/api/objects?kind=site&limit=500') })
  if (customers.isLoading) return <Spinner />
  const rows = customers.data ?? []
  const totalSites = sites.data?.length ?? 0

  return (
    <>
      <div className="tiles" style={{ marginBottom: 18 }}>
        <Tile label="Kunden" value={rows.length} />
        <Tile label="Liegenschaften" value={totalSites} />
        <Tile label="Objekte darunter" value={rows.reduce((n, c) => n + (c.descendants ?? 0), 0) - totalSites} sub="Gebäude und Einheiten" />
        <Tile
          label="Aufgaben nicht erledigt"
          value={rows.reduce((n, c) => n + (c.missedTasks ?? 0), 0)}
          tone={rows.some((c) => (c.missedTasks ?? 0) > 0) ? 'alert' : 'good'}
        />
      </div>
      <section className="card">
        <div className="card-head">
          <h2>Kunden</h2>
          <button type="button" className="btn secondary small" onClick={onNewCustomer}>
            Neuer Kunde
          </button>
        </div>
        {rows.length === 0 ? (
          <EmptyState title="Noch keine Kunden" hint="Ein Kunde ist der Eigentümer oder Auftraggeber, dem Liegenschaften zugeordnet werden." />
        ) : (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Kunde</th>
                  <th>Kontakt</th>
                  <th className="nowrap">Objekte darunter</th>
                  <th className="nowrap">Aufgaben offen</th>
                  <th className="nowrap">Aufgaben nicht erledigt</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link to={`/objekte/${c.id}`} className="strong">
                        {c.name}
                      </Link>
                      {c.number && <div className="muted small">Nr. {c.number}</div>}
                    </td>
                    <td className="small">{c.contact ?? <span className="muted">–</span>}</td>
                    <td className="nowrap">{c.descendants ?? 0}</td>
                    <td className="nowrap">{c.openTasks ?? 0}</td>
                    <td className="nowrap">{(c.missedTasks ?? 0) > 0 ? <span className="badge missed">{c.missedTasks}</span> : <span className="muted">0</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}

// ---------------------------------------------------------------- Detail

type Tab = 'children' | 'tasks' | 'plans' | 'issues' | 'documents' | 'youtilly' | 'history'

function NodeDetail({ node, onNewChild }: { node: ObjectDetail; onNewChild: () => void }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const toast = useToast()
  const [tab, setTab] = useState<Tab>('children')
  const [editing, setEditing] = useState(false)
  const [moving, setMoving] = useState(false)
  const [boardOpen, setBoardOpen] = useState(false)
  const [blocking, setBlocking] = useState(false)
  const [openTask, setOpenTask] = useState<string | null>(null)
  const [showAdhoc, setShowAdhoc] = useState(false)

  // A leaf has nothing below it; open its tasks straight away.
  useEffect(() => {
    setTab('children')
  }, [node.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['object'] })
    await queryClient.invalidateQueries({ queryKey: ['objects'] })
    await queryClient.invalidateQueries({ queryKey: ['object-search'] })
  }

  const restore = useMutation({
    mutationFn: (id: string) => api(`/api/objects/${id}/restore`, { method: 'POST' }),
    onSuccess: invalidate,
  })
  const archive = useMutation({
    mutationFn: (target: ObjectNode) => api<{ archivedNodes: number }>(`/api/objects/${target.id}`, { method: 'DELETE' }),
    onSuccess: async (result, target) => {
      toast(
        result.archivedNodes > 1
          ? `„${target.name}“ und ${result.archivedNodes - 1} untergeordnete Objekte archiviert.`
          : `„${target.name}“ archiviert.`,
        { action: { label: 'Rückgängig', onAction: () => restore.mutate(target.id) } },
      )
      await invalidate()
      if (target.id === node.id) navigate(node.parentId ? `/objekte/${node.parentId}` : '/objekte')
    },
  })

  const address = formatAddress(node.context)
  const isCustomer = node.kind === 'customer'
  const canHaveChildren = childKinds(node.kind).length > 0

  return (
    <>
      <nav className="crumbs" aria-label="Pfad">
        <Link to="/objekte">Alle Objekte</Link>
        {node.breadcrumb.map((crumb) => (
          <span key={crumb.id} style={{ display: 'contents' }}>
            <span className="sep">›</span>
            <Link to={`/objekte/${crumb.id}`}>
              <KindBadge kind={crumb.kind} />
              {crumb.name}
            </Link>
          </span>
        ))}
      </nav>

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-body">
          <div className="spread" style={{ alignItems: 'flex-start' }}>
            <div style={{ minWidth: 0 }}>
              <div className="muted small" style={{ marginBottom: 4 }}>
                {KIND_LABEL[node.kind]}
                {node.number ? `, Nr. ${node.number}` : ''}
                {node.externalRef ? `, Youtilly ${node.externalRef}` : ''}
                {node.economicUnit ? `, Wirtschaftseinheit ${node.economicUnit}` : ''}
              </div>
              <h2 style={{ fontSize: 22, marginBottom: 4 }}>{node.name}</h2>
              {address && (
                <div className="muted">
                  {address}{' '}
                  <a className="small" href={`https://maps.apple.com/?q=${encodeURIComponent(address)}`} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <Icon name="pin" size={13} /> Karte
                  </a>
                </div>
              )}
              {node.contact && <div className="small" style={{ marginTop: 4 }}>{node.contact}</div>}
              {node.notes && <div className="small muted" style={{ marginTop: 6 }}>{node.notes}</div>}
              {(node.attributes ?? []).length > 0 && (
                <dl className="attr-list">
                  {node.attributes!.map((a) => (
                    <div key={a.label}>
                      <dt>{a.label}</dt>
                      <dd>{a.value || '–'}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {node.archivedAt && (
                <div style={{ marginTop: 8 }}>
                  <span className="badge skipped">archiviert am {formatDate(node.archivedAt.slice(0, 10))}</span>{' '}
                  <button type="button" className="btn ghost small" onClick={() => restore.mutate(node.id)}>
                    Wiederherstellen
                  </button>
                </div>
              )}
            </div>
            <div className="actions">
              {!isCustomer && (
                <Link className="btn secondary small" to={`/nachweise?objectId=${node.id}`}>
                  <Icon name="document" size={16} /> Nachweis
                </Link>
              )}
              {!isCustomer && (
                <button type="button" className="btn secondary small" onClick={() => setShowAdhoc(true)}>
                  <Icon name="bolt" size={16} /> Sonderaufgabe
                </button>
              )}
              {canHaveChildren && (
                <button type="button" className="btn small" onClick={onNewChild}>
                  <Icon name="plus" size={16} /> Neues Unterobjekt
                </button>
              )}
              <button type="button" className="btn ghost small" onClick={() => setEditing(true)}>
                <Icon name="edit" size={16} /> Bearbeiten
              </button>
              {MOVE_TARGET[node.kind] && (
                <button type="button" className="btn ghost small" onClick={() => setMoving(true)}>
                  <Icon name="move" size={16} /> {MOVE_TARGET[node.kind]!.button}
                </button>
              )}
              {(node.kind === 'site' || node.kind === 'building') && (
                <button type="button" className="btn ghost small" onClick={() => setBoardOpen(true)}>
                  <Icon name="qr" size={16} /> Aushang
                </button>
              )}
              {!isCustomer && (
                <button type="button" className="btn ghost small" onClick={() => setBlocking(true)}>
                  <Icon name="lock" size={16} /> Sperrfenster
                </button>
              )}
              {!node.archivedAt && (
                <button
                  type="button"
                  className="btn ghost small"
                  onClick={() => {
                    const below = node.stats.descendants
                    if (
                      confirm(
                        below > 0
                          ? `„${node.name}“ samt ${below} untergeordneten Objekten archivieren? Nachweise bleiben erhalten.`
                          : `„${node.name}“ archivieren? Nachweise bleiben erhalten.`,
                      )
                    ) {
                      archive.mutate(node)
                    }
                  }}
                >
                  <Icon name="archive" size={16} /> Archivieren
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <ObjectNotes node={node} onBlock={() => setBlocking(true)} onBoard={() => setBoardOpen(true)} />
      {isCustomer && <CustomerAccess node={node} />}

      {!isCustomer && (
        <div className="tiles" style={{ marginBottom: 18 }}>
          <Tile label="Aufgaben offen" value={node.stats.openTasks} sub="geplant, hier und darunter" />
          <Tile label="Aufgaben nicht erledigt" value={node.stats.overdueTasks} tone={node.stats.overdueTasks > 0 ? 'alert' : 'good'} sub="noch ohne Entscheidung" />
          <Tile
            label="Erfüllung (30 Tage)"
            value={
              node.stats.doneMonth + node.stats.missedMonth > 0
                ? `${Math.round((node.stats.doneMonth / (node.stats.doneMonth + node.stats.missedMonth)) * 100)} %`
                : '–'
            }
            sub={`${node.stats.doneMonth} erledigt, ${node.stats.missedMonth} nicht`}
          />
          <Tile label="Pläne aktiv / Mängel offen" value={`${node.stats.activePlans} / ${node.stats.openIssues}`} sub="hier und darunter" tone={node.stats.openIssues > 0 ? 'alert' : undefined} />
        </div>
      )}

      <div className="tabs" role="tablist">
        {(
          [
            ['children', 'Unterobjekte', node.children.length, 'tree'],
            ['tasks', 'Aufgaben anzeigen', null, 'list'],
            ['plans', 'Pläne', node.stats.activePlans, 'repeat'],
            ['issues', 'Mängel', node.stats.openIssues, 'warning'],
            ['documents', 'Dokumente', null, 'file'],
            ...(isCustomer ? [] : [['youtilly', 'Youtilly-Positionen', null, 'link']]),
            ['history', 'Verlauf', null, 'clock'],
          ] as [Tab, string, number | null, IconName][]
        ).map(([key, label, n, icon]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            // Aufgaben live in the task list; this tab opens it filtered to here.
            onClick={() => (key === 'tasks' ? navigate(`/aufgaben?objekt=${node.id}`) : setTab(key))}
          >
            <Icon name={icon} size={15} /> {label}
            {n !== null && <span className="n">{n}</span>}
          </button>
        ))}
      </div>

      {tab === 'children' && <ChildrenTab node={node} onArchive={(child) => archive.mutate(child)} onRestore={(id) => restore.mutate(id)} onNewChild={onNewChild} />}
      {tab === 'plans' && <PlansTab node={node} />}
      {tab === 'issues' && <IssuesTab objectId={node.id} />}
      {tab === 'documents' && <DocumentsTab node={node} />}
      {tab === 'youtilly' && <ServiceRefsTab node={node} />}
      {tab === 'history' && (
        <section className="card">
          <div className="card-head">
            <div>
              <h2>Verlauf</h2>
              <div className="muted small">Änderungen an diesem Objekt, seinen Dokumenten und Aushängen. Aufgaben haben ihren eigenen Verlauf.</div>
            </div>
          </div>
          <div className="card-body">
            <History entity="object" entityId={node.id} />
          </div>
        </section>
      )}

      <ObjectFormModal open={editing} node={node} onClose={() => setEditing(false)} onSaved={() => setEditing(false)} />
      <MoveModal open={moving} node={node} onClose={() => setMoving(false)} />
      <BoardModal open={boardOpen} node={node} onClose={() => setBoardOpen(false)} />
      <BlockModal open={blocking} node={node} onClose={() => setBlocking(false)} />
      <TaskModal
        taskId={openTask}
        onClose={() => setOpenTask(null)}
        onChanged={async (message) => {
          if (message) toast(message)
          setOpenTask(null)
          await queryClient.invalidateQueries({ queryKey: ['occurrences'] })
          await invalidate()
        }}
      />
      <AdhocModal
        open={showAdhoc}
        defaultObjectId={node.id}
        onClose={() => setShowAdhoc(false)}
        onCreated={async () => {
          setShowAdhoc(false)
          toast('Sonderaufgabe angelegt.')
          await queryClient.invalidateQueries({ queryKey: ['occurrences'] })
          await invalidate()
        }}
      />
    </>
  )
}

function ChildrenTab({
  node,
  onArchive,
  onRestore,
  onNewChild,
}: {
  node: ObjectDetail
  onArchive: (child: ObjectNode) => void
  onRestore: (id: string) => void
  onNewChild: () => void
}) {
  const kinds = [...new Set(node.children.map((c) => c.kind))]
  return (
    <section className="card">
      {node.children.length === 0 ? (
        <EmptyState
          title={node.kind === 'component' ? 'Ein Bauteil ist die unterste Ebene' : 'Noch nichts darunter'}
          hint={
            node.kind === 'component'
              ? 'Aufgaben und Mängel hängen direkt hier.'
              : `Mögliche Ebenen: ${childKinds(node.kind).map((k) => KIND_LABEL[k]).join(', ')}. Ebenen dürfen übersprungen werden.`
          }
          action={
            childKinds(node.kind).length > 0 ? (
              <button type="button" className="btn" onClick={onNewChild}>
                Neues Unterobjekt
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>{kinds.length === 1 ? KIND_PLURAL[kinds[0]!] : 'Objekt'}</th>
                <th>Nummer / ID</th>
                <th>Adresse</th>
                <th className="nowrap" title="Gebäude und Einheiten unterhalb">Objekte darunter</th>
                <th className="nowrap" title="Geplante, noch offene Aufgaben an diesem Objekt und darunter">Aufgaben offen</th>
                <th className="nowrap" title="Nicht erledigte Aufgaben ohne Entscheidung">Aufgaben nicht erledigt</th>
                <th className="nowrap" title="In den letzten 30 Tagen erledigt">Aufgaben erledigt, 30 Tage</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {node.children.map((child) => (
                <tr key={child.id}>
                  <td>
                    <Link to={`/objekte/${child.id}`} className="strong" style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                      <KindBadge kind={child.kind} />
                      {child.name}
                    </Link>
                    {child.notes && <div className="muted small clamp-2">{child.notes}</div>}
                  </td>
                  <td className="small">
                    {child.number && <div>Nr. {child.number}</div>}
                    {child.externalRef && <div className="muted">{child.externalRef}</div>}
                    {!child.number && !child.externalRef && <span className="muted">–</span>}
                  </td>
                  <td className="small">{formatAddress(child) || <span className="muted">–</span>}</td>
                  <td className="nowrap">{child.descendants ?? 0}</td>
                  <td className="nowrap">{child.openTasks ?? 0}</td>
                  <td className="nowrap">{(child.missedTasks ?? 0) > 0 ? <span className="badge missed">{child.missedTasks}</span> : <span className="muted">0</span>}</td>
                  <td className="nowrap">{child.doneMonth ?? 0}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button type="button" className="btn ghost small" onClick={() => onArchive(child)}>
                      Archivieren
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {node.archivedChildren.length > 0 && (
        <div className="card-body" style={{ borderTop: '1px solid var(--line-soft)' }}>
          <div className="muted small" style={{ marginBottom: 6 }}>
            Archiviert
          </div>
          {node.archivedChildren.map((child) => (
            <div key={child.id} className="spread small" style={{ padding: '4px 0' }}>
              <span style={{ opacity: 0.6 }}>
                <KindBadge kind={child.kind} /> {child.name}
              </span>
              <button type="button" className="btn ghost small" onClick={() => onRestore(child.id)}>
                Wiederherstellen
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function PlansTab({ node }: { node: ObjectDetail }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [draft, setDraft] = useState<PlanDraft | null>(null)
  const plans = useQuery({ queryKey: ['plans', node.id], queryFn: () => api<Plan[]>(`/api/plans?objectId=${node.id}`) })

  const reactivate = useMutation({
    mutationFn: (id: string) => api(`/api/plans/${id}`, { method: 'PATCH', body: { active: true } }),
    onSuccess: async () => {
      toast('Plan wieder aktiv.')
      await queryClient.invalidateQueries({ queryKey: ['plans'] })
    },
  })
  const deactivate = useMutation({
    mutationFn: (id: string) => api<{ removedFutureTasks: number }>(`/api/plans/${id}`, { method: 'DELETE' }),
    onSuccess: async (result, id) => {
      toast(`Plan deaktiviert. ${result.removedFutureTasks} künftige offene Aufgaben entfernt.`, {
        action: { label: 'Rückgängig', onAction: () => reactivate.mutate(id) },
      })
      await queryClient.invalidateQueries({ queryKey: ['plans'] })
      await queryClient.invalidateQueries({ queryKey: ['object'] })
    },
  })

  return (
    <section className="card">
      <div className="card-head">
        <h2>Aufgabenpläne an und unterhalb von „{node.name}“</h2>
        <div className="actions">
          <Link className="btn secondary small" to={`/plaene?objekt=${node.id}`}>
            In der Planliste
          </Link>
          <button type="button" className="btn small" onClick={() => setDraft(emptyPlanDraft(node.id))}>
            <Icon name="plus" size={15} /> Neuer Plan hier
          </button>
        </div>
      </div>
      {plans.isLoading ? (
        <Spinner />
      ) : !plans.data || plans.data.length === 0 ? (
        <EmptyState
          title="Keine Pläne"
          hint={
            node.kind === 'customer'
              ? 'Ein Plan am Kunden gilt für jede Liegenschaft darunter, z. B. Gartenpflege überall.'
              : 'Ein Plan hier kann für dieses Objekt gelten oder für jedes Gebäude oder jede Einheit darunter.'
          }
          action={
            <button type="button" className="btn" onClick={() => setDraft(emptyPlanDraft(node.id))}>
              Neuer Plan hier
            </button>
          }
        />
      ) : (
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>Leistung</th>
                <th>Wo</th>
                <th>Rhythmus</th>
                <th>Zuständig</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {plans.data.map((plan) => (
                <tr key={plan.id} style={plan.active ? undefined : { opacity: 0.5 }}>
                  <td>
                    <span className="strong">{plan.templateName}</span>
                    {!plan.active && (
                      <span className="badge skipped" style={{ marginLeft: 8 }}>
                        inaktiv
                      </span>
                    )}
                  </td>
                  <td className="small">
                    {plan.objectId === node.id ? (
                      <span>hier</span>
                    ) : (
                      <Link to={`/objekte/${plan.objectId}`}>{plan.objectKind === 'site' || plan.objectKind === 'customer' ? plan.objectName : plan.path.join(' › ')}</Link>
                    )}
                    {spreadLabel(plan) && <div style={{ color: 'var(--blue-700)' }}>{spreadLabel(plan)}</div>}
                  </td>
                  <td className="small">
                    {plan.recurrenceLabel}
                    {plan.dueTime && <div className="muted">bis {plan.dueTime} Uhr</div>}
                    {plan.seasonLabel && <div className="muted">Saison {plan.seasonLabel}</div>}
                  </td>
                  <td>{plan.assigneeId ? <Link to={`/mitarbeiter/${plan.assigneeId}`}>{plan.assigneeName}</Link> : plan.teamName ? `Gruppe ${plan.teamName}` : <span className="muted">nicht zugewiesen</span>}</td>
                  <td style={{ textAlign: 'right' }} className="nowrap">
                    <button type="button" className="btn ghost small" onClick={() => setDraft(draftFromPlan(plan))}>
                      <Icon name="edit" size={15} /> Bearbeiten
                    </button>{' '}
                    {plan.active ? (
                      <button type="button" className="btn ghost small" onClick={() => deactivate.mutate(plan.id)}>
                        Deaktivieren
                      </button>
                    ) : (
                      <button type="button" className="btn ghost small" onClick={() => reactivate.mutate(plan.id)}>
                        Aktivieren
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <PlanFormModal draft={draft} onClose={() => setDraft(null)} onSaved={() => setDraft(null)} />
    </section>
  )
}

function IssuesTab({ objectId }: { objectId: string }) {
  const issues = useQuery({ queryKey: ['issues', 'object', objectId], queryFn: () => api<Issue[]>(`/api/issues?status=all&objectId=${objectId}`) })
  return (
    <section className="card">
      <div className="card-head">
        <h2>Mängel</h2>
        <Link className="btn secondary small" to="/maengel">
          Mängelliste
        </Link>
      </div>
      {issues.isLoading ? (
        <Spinner />
      ) : !issues.data || issues.data.length === 0 ? (
        <EmptyState title="Keine Mängel gemeldet" />
      ) : (
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>Gemeldet</th>
                <th>Problem</th>
                <th>Wo</th>
                <th className="nowrap">Status</th>
              </tr>
            </thead>
            <tbody>
              {issues.data.map((issue) => (
                <tr key={issue.id}>
                  <td className="nowrap small">{formatDateTime(issue.createdAt)}</td>
                  <td className="strong">{issue.title}</td>
                  <td className="small">{issue.path.join(' › ') || 'Liegenschaft'}</td>
                  <td className="nowrap">
                    <StatusBadge status={issue.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

// ---------------------------------------------------------------- Formulare

function ObjectFormModal({
  open,
  parent,
  node,
  onClose,
  onSaved,
}: {
  open: boolean
  /** For a new node: where it goes. */
  parent?: ObjectNode | null
  /** For editing. */
  node?: ObjectDetail
  onClose: () => void
  onSaved: (node: ObjectNode) => void
}) {
  const queryClient = useQueryClient()
  // Edit and new forms can be mounted at the same time, so the id must be unique or Speichern submits the wrong one.
  const formId = useId()
  const [error, setError] = useState<string | null>(null)
  const kinds = node ? [node.kind] : childKinds(parent?.kind ?? null)
  const [kind, setKind] = useState<ObjectKind>(kinds[0]!)
  useEffect(() => setKind(kinds[0]!), [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      node ? api<ObjectNode>(`/api/objects/${node.id}`, { method: 'PATCH', body }) : api<ObjectNode>('/api/objects', { method: 'POST', body }),
    onSuccess: async (saved) => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['object'] })
      await queryClient.invalidateQueries({ queryKey: ['objects'] })
      await queryClient.invalidateQueries({ queryKey: ['object-search'] })
      onSaved(saved)
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const showAddress = kind === 'site' || kind === 'building'
  const title = node ? `${KIND_LABEL[node.kind]} bearbeiten` : parent ? `Neu unter „${parent.name}“` : 'Neuer Kunde'

  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" form={formId} className="btn" disabled={save.isPending}>
            Speichern
          </button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      <form
        id={formId}
        key={`${node?.id ?? 'new'}-${parent?.id ?? 'root'}`}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault()
          const form = new FormData(event.currentTarget)
          const value = (name: string) => (form.get(name) as string)?.trim() || undefined
          save.mutate({
            ...(node ? {} : { kind, parentId: parent?.id ?? null }),
            name: value('name'),
            number: node ? (value('number') ?? '') : value('number'),
            externalRef: node ? (value('externalRef') ?? '') : value('externalRef'),
            street: node ? (value('street') ?? '') : value('street'),
            zip: node ? (value('zip') ?? '') : value('zip'),
            city: node ? (value('city') ?? '') : value('city'),
            contact: node ? (value('contact') ?? '') : value('contact'),
            notes: node ? (value('notes') ?? '') : value('notes'),
            accessNote: node ? (value('accessNote') ?? '') : value('accessNote'),
            economicUnit: node ? (value('economicUnit') ?? '') : value('economicUnit'),
            attributes: parseAttributes(String(form.get('attributes') ?? '')),
          })
        }}
      >
        {!node && kinds.length > 1 && (
          <Field label="Ebene" hint="Ebenen dürfen übersprungen werden, eine Einheit kann direkt an der Liegenschaft hängen.">
            <select value={kind} onChange={(event) => setKind(event.target.value as ObjectKind)}>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Bezeichnung" hint={kind === 'unit' ? 'z. B. „Wohnung 3.02“' : kind === 'site' ? 'z. B. „Lindenstraße 14“' : undefined}>
          <input name="name" required autoFocus defaultValue={node?.name} />
        </Field>
        <div className="row">
          <Field label={kind === 'unit' ? 'Wohnungsnummer' : 'Eigene Nummer'} hint="Wie in Ihren Unterlagen; wird beim Import zur Zuordnung genutzt.">
            <input name="number" defaultValue={node?.number ?? ''} />
          </Field>
          <Field label="Youtilly-ID" hint="ID im System des Kunden.">
            <input name="externalRef" defaultValue={node?.externalRef ?? ''} />
          </Field>
          {kind !== 'customer' && (
            <Field label="Wirtschaftseinheit" hint="Wie beim Kunden geführt.">
              <input name="economicUnit" defaultValue={node?.economicUnit ?? ''} />
            </Field>
          )}
        </div>
        {(showAddress || node?.street) && (
          <>
            <Field label="Straße und Hausnummer">
              <input name="street" defaultValue={node?.street ?? ''} />
            </Field>
            <div className="row">
              <Field label="PLZ">
                <input name="zip" defaultValue={node?.zip ?? ''} />
              </Field>
              <Field label="Ort">
                <input name="city" defaultValue={node?.city ?? ''} />
              </Field>
            </div>
          </>
        )}
        {kind === 'customer' && (
          <Field label="Kontakt" hint="Ansprechpartner, Telefon, E-Mail.">
            <input name="contact" defaultValue={node?.contact ?? ''} />
          </Field>
        )}
        {kind !== 'customer' && (
          <Field label="Zutritt" hint="Wie man hineinkommt. Erscheint bei jeder Aufgabe hier und darunter in der App, z. B. „Schlüssel im Kasten links neben der Tür“.">
            <input name="accessNote" defaultValue={node?.accessNote ?? ''} />
          </Field>
        )}
        <Field label="Notiz" hint="Interne Notiz zum Objekt.">
          <textarea name="notes" defaultValue={node?.notes ?? ''} />
        </Field>
        <Field label="Weitere Angaben" hint="Eine Angabe pro Zeile, Bezeichnung und Wert mit Doppelpunkt, z. B. „Baujahr: 1972“ oder „Anzahl Wohnungen: 24“.">
          <textarea name="attributes" defaultValue={(node?.attributes ?? []).map((a) => `${a.label}: ${a.value}`).join('\n')} placeholder={'Baujahr: 1972\nHeizung: Fernwärme'} />
        </Field>
      </form>
    </Modal>
  )
}

/** Where a node can be moved to: a Liegenschaft to another Kunde, a Gebäude to another Liegenschaft, an Einheit to another Gebäude. */
const MOVE_TARGET: Partial<Record<ObjectKind, { kind: ObjectKind; button: string; field: string; hint: string }>> = {
  site: {
    kind: 'customer',
    button: 'Anderem Kunden zuordnen',
    field: 'Neuer Kunde',
    hint: 'Bei einem Eigentümerwechsel wandert die Liegenschaft mit allen Gebäuden, Aufgaben, Fotos und Nachweisen zum neuen Kunden. Alte Nachweise bleiben unverändert.',
  },
  building: {
    kind: 'site',
    button: 'In andere Liegenschaft verschieben',
    field: 'Neue Liegenschaft',
    hint: 'Das Gebäude wandert mit seinen Einheiten, Plänen, Aufgaben und Fotos in die gewählte Liegenschaft. Alte Nachweise bleiben unverändert.',
  },
  unit: {
    kind: 'building',
    button: 'In anderes Gebäude verschieben',
    field: 'Neues Gebäude',
    hint: 'Die Einheit wandert mit ihren Plänen, Aufgaben und Fotos in das gewählte Gebäude.',
  },
}

/** A node changes place in the tree with its whole history, e.g. a Liegenschaft that changes hands. */
function MoveModal({ open, node, onClose }: { open: boolean; node: ObjectDetail; onClose: () => void }) {
  const config = MOVE_TARGET[node.kind]
  const queryClient = useQueryClient()
  const toast = useToast()
  const [target, setTarget] = useState('')
  const [error, setError] = useState<string | null>(null)

  const move = useMutation({
    mutationFn: () => api<{ movedNodes: number }>(`/api/objects/${node.id}`, { method: 'PATCH', body: { parentId: target } }),
    onSuccess: async (result) => {
      toast(`„${node.name}“ umgehängt, ${result.movedNodes} Objekte samt Historie.`)
      await queryClient.invalidateQueries({ queryKey: ['object'] })
      await queryClient.invalidateQueries({ queryKey: ['objects'] })
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Umhängen fehlgeschlagen.'),
  })

  return (
    <Modal
      open={open}
      title={config?.button ?? 'Verschieben'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="btn" disabled={!target || move.isPending} onClick={() => move.mutate()}>
            Verschieben
          </button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      <p className="small muted" style={{ marginTop: 0 }}>
        {config?.hint}
      </p>
      <Field label={config?.field ?? 'Ziel'}>
        <ObjectPicker value={target} onChange={(id) => setTarget(id)} taskable={false} kind={config?.kind} placeholder="Suchen …" />
      </Field>
    </Modal>
  )
}

function ImportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [csv, setCsv] = useState('')
  const [mapping, setMapping] = useState<Record<string, number>>({})
  const [result, setResult] = useState<ObjectImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = useMutation({
    mutationFn: (dryRun: boolean) => api<ObjectImportResult>('/api/objects/import', { method: 'POST', body: { csv, mapping, dryRun } }),
    onSuccess: async (data) => {
      setResult(data)
      setError(null)
      if (!data.dryRun) {
        await queryClient.invalidateQueries({ queryKey: ['objects'] })
        await queryClient.invalidateQueries({ queryKey: ['object'] })
      }
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Import fehlgeschlagen.'),
  })

  const created = result ? Object.entries(result.created).filter(([, n]) => n > 0) : []

  return (
    <Modal
      open={open}
      title="Objekte aus Excel importieren"
      onClose={() => {
        setCsv('')
        setResult(null)
        onClose()
      }}
      footer={
        <>
          <button type="button" className="btn secondary" onClick={onClose}>
            Schließen
          </button>
          {!result?.dryRun && !result && (
            <button type="button" className="btn" disabled={!csv || run.isPending} onClick={() => run.mutate(true)}>
              Prüfen
            </button>
          )}
          {result?.dryRun && (
            <button type="button" className="btn" disabled={run.isPending} onClick={() => run.mutate(false)}>
              {run.isPending ? 'Wird importiert …' : 'Jetzt importieren'}
            </button>
          )}
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      {result ? (
        <>
          <Notice kind={result.dryRun ? 'info' : 'success'}>
            {result.dryRun ? 'Vorschau, noch nichts geschrieben.' : 'Import abgeschlossen.'} {result.rows} Zeilen gelesen.
            {created.length > 0 && (
              <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                {created.map(([kind, n]) => (
                  <li key={kind}>
                    {n} {KIND_PLURAL[kind as ObjectKind]} {result.dryRun ? 'würden angelegt' : 'angelegt'}
                  </li>
                ))}
                {result.updatedRefs > 0 && <li>{result.updatedRefs} Youtilly-IDs ergänzt</li>}
              </ul>
            )}
            {created.length === 0 && result.updatedRefs === 0 && <div>Alles schon vorhanden, nichts zu tun.</div>}
          </Notice>
          {result.warnings.length > 0 && (
            <Notice kind="warn">
              <strong>Hinweise:</strong>
              <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                {result.warnings.slice(0, 8).map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </Notice>
          )}
          <div className="small muted">Erkannte Spalten: {result.columns.join(', ')}</div>
        </>
      ) : (
        <>
          <p className="small" style={{ marginTop: 0 }}>
            Eine Zeile je tiefstem Objekt; Ebenen darüber werden beim ersten Vorkommen angelegt. Erkannte Spalten:{' '}
            <strong>Kunde, Liegenschaft</strong> (Pflicht), Straße, PLZ, Ort, Gebäude, Einheit, Nummer, Youtilly-ID, Wirtschaftseinheit,
            Zutritt, Notiz. Excel-Datei direkt oder CSV. Die Datei aus „Als Excel“ lässt sich bearbeiten und wieder einlesen.
          </p>
          <pre className="small muted" style={{ background: 'var(--canvas)', padding: 12, borderRadius: 8, overflowX: 'auto' }}>
            {`Kunde;Liegenschaft;Straße;PLZ;Ort;Gebäude;Einheit;Nummer;Youtilly-ID
LEG Wohnen NRW;Aderweg 1–7;Aderweg 1–7;51371;Leverkusen;Haus 1;Wohnung 1.01;1.01;LEG-4711-101
LEG Wohnen NRW;Aderweg 1–7;Aderweg 1–7;51371;Leverkusen;Haus 1;Wohnung 1.02;1.02;LEG-4711-102
WEG Lindenstraße 14;Lindenstraße 14;Lindenstraße 14;44135;Dortmund;;;;`}
          </pre>
          <Field label="Excel- oder CSV-Datei">
            <input
              ref={fileRef}
              type="file"
              accept={TABLE_ACCEPT}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) readTableFile(file).then(setCsv, (err) => setError(err instanceof ApiError ? err.message : 'Die Datei konnte nicht gelesen werden.'))
              }}
            />
          </Field>
          <ColumnMapper csv={csv} target="objects" onChange={setMapping} />
        </>
      )}
    </Modal>
  )
}

// ---------------------------------------------------------------- Zutritt, Sperrfenster, Aushang

/** The things a Verwalter must see before planning anything here. */
function ObjectNotes({ node, onBlock, onBoard }: { node: ObjectDetail; onBlock: () => void; onBoard: () => void }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const today = todayIso()
  const unblock = useMutation({
    mutationFn: () => api(`/api/objects/${node.id}/block`, { method: 'DELETE' }),
    onSuccess: async () => {
      toast('Sperrfenster aufgehoben. Neue Aufgaben entstehen wieder nach Plan.')
      await queryClient.invalidateQueries({ queryKey: ['object'] })
    },
  })

  const blocked = node.blockedUntil && node.blockedUntil >= today
  const items = [
    blocked && (
      <div key="block" className="note-strip warn">
        <Icon name="lock" size={16} />
        <span>
          <strong>Sperrfenster</strong> {formatDate(node.blockedFrom!)} bis {formatDate(node.blockedUntil!)}: {node.blockedReason}. In dieser Zeit
          entstehen hier und darunter keine Aufgaben.
        </span>
        <button type="button" className="btn ghost small" onClick={() => unblock.mutate()}>
          Aufheben
        </button>
      </div>
    ),
    node.context.accessNote && (
      <div key="access" className="note-strip">
        <Icon name="key" size={16} />
        <span>
          <strong>Zutritt</strong> {node.context.accessNote}
          {node.accessNote ? '' : ' (von übergeordnetem Objekt)'}
        </span>
      </div>
    ),
    node.access.count > 0 && (
      <div key="noaccess" className="note-strip warn">
        <Icon name="warning" size={16} />
        <span>
          <strong>{node.access.count}× kein Zutritt</strong> in 90 Tagen, zuletzt {formatDate(node.access.recent[0]!.dueDate)}
          {node.access.recent[0]!.label ? ` (${node.access.recent[0]!.label})` : ''}: {node.access.recent[0]!.textOption}
        </span>
        {!blocked && (
          <button type="button" className="btn ghost small" onClick={onBlock}>
            Sperrfenster setzen
          </button>
        )}
      </div>
    ),
    (node.boardUrl || node.feedback.good + node.feedback.bad + node.feedback.reports > 0) && (
      <div key="board" className="note-strip">
        <Icon name="qr" size={16} />
        <span>
          <strong>Aushang</strong> {node.boardUrl ? 'aktiv' : 'nicht aktiv'}. Bewohner in 30 Tagen: {node.feedback.good} sauber,{' '}
          {node.feedback.bad} nicht sauber, {node.feedback.reports} Meldungen.
        </span>
        <button type="button" className="btn ghost small" onClick={onBoard}>
          Öffnen
        </button>
      </div>
    ),
  ].filter(Boolean)

  if (items.length === 0) return null
  return <div className="stack" style={{ gap: 8, marginBottom: 18 }}>{items}</div>
}

function BlockModal({ open, node, onClose }: { open: boolean; node: ObjectDetail; onClose: () => void }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [error, setError] = useState<string | null>(null)
  const block = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<{ skippedTasks: number }>(`/api/objects/${node.id}/block`, { method: 'POST', body }),
    onSuccess: async (result) => {
      toast(`Sperrfenster gesetzt. ${result.skippedTasks} offene Aufgaben im Zeitraum entfallen.`)
      await queryClient.invalidateQueries({ queryKey: ['object'] })
      await queryClient.invalidateQueries({ queryKey: ['occurrences'] })
      setError(null)
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })
  return (
    <Modal
      open={open}
      title={`Sperrfenster für „${node.name}“`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" form="block-form" className="btn" disabled={block.isPending}>
            Sperren
          </button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      <p className="small muted" style={{ marginTop: 0 }}>
        Für Modernisierung, Leerstand oder Baustelle. Offene Aufgaben im Zeitraum werden auf „entfällt“ gesetzt, neue entstehen erst nach
        dem Ende wieder. Gilt für dieses Objekt und alles darunter.
      </p>
      <form
        id="block-form"
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault()
          const form = new FormData(event.currentTarget)
          block.mutate({ from: form.get('from'), until: form.get('until'), reason: form.get('reason') })
        }}
      >
        <div className="row">
          <Field label="Von">
            <input name="from" type="date" required defaultValue={node.blockedFrom ?? todayIso()} />
          </Field>
          <Field label="Bis">
            <input name="until" type="date" required defaultValue={node.blockedUntil ?? addDays(todayIso(), 27)} />
          </Field>
        </div>
        <Field label="Grund" hint="Steht an den entfallenen Aufgaben und im Nachweis.">
          <input name="reason" required defaultValue={node.blockedReason ?? ''} placeholder="z. B. Modernisierung, Wasser abgestellt" />
        </Field>
      </form>
    </Modal>
  )
}

function BoardModal({ open, node, onClose }: { open: boolean; node: ObjectDetail; onClose: () => void }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const feedback = useQuery({
    queryKey: ['object-feedback', node.id],
    queryFn: () => api<{ boardUrl: string | null; feedback: ResidentFeedback[] }>(`/api/objects/${node.id}/feedback`),
    enabled: open,
  })
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['object'] })
    await queryClient.invalidateQueries({ queryKey: ['object-feedback', node.id] })
  }
  const create = useMutation({
    mutationFn: () => api<{ url: string }>(`/api/objects/${node.id}/board`, { method: 'POST' }),
    onSuccess: async () => {
      toast('Aushang aktiv. Jetzt drucken und aufhängen.')
      await refresh()
    },
  })
  const remove = useMutation({
    mutationFn: () => api(`/api/objects/${node.id}/board`, { method: 'DELETE' }),
    onSuccess: async () => {
      toast('Aushang abgeschaltet. Der QR-Code führt ins Leere.')
      await refresh()
    },
  })
  const url = feedback.data?.boardUrl ?? node.boardUrl

  return (
    <Modal open={open} title={`Aushang für „${node.name}“`} onClose={onClose}>
      <p className="small muted" style={{ marginTop: 0 }}>
        Ein QR-Code im Treppenhaus. Bewohner sehen, wann die Gemeinschaftsflächen zuletzt gepflegt wurden und wann der nächste Termin ist,
        und können „sauber“ oder „nicht sauber“ sagen oder etwas melden. Ohne Anmeldung, ohne Namen von Mitarbeitenden. Wohnungen und
        alles darin bleiben unsichtbar.
      </p>
      {url ? (
        <>
          <Field label="Adresse hinter dem QR-Code">
            <input readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
          </Field>
          <div className="actions" style={{ marginBottom: 16 }}>
            <a className="btn" href={`/api/objects/${node.id}/board/print`} target="_blank" rel="noreferrer">
              <Icon name="print" /> Zum Drucken öffnen
            </a>
            <a className="btn secondary" href={url} target="_blank" rel="noreferrer">
              Ansehen wie ein Bewohner
            </a>
            <button type="button" className="btn ghost" onClick={() => create.mutate()}>
              Neuen Code erzeugen
            </button>
            <button type="button" className="btn ghost" onClick={() => remove.mutate()}>
              Abschalten
            </button>
          </div>
        </>
      ) : (
        <button type="button" className="btn" onClick={() => create.mutate()} disabled={create.isPending} style={{ marginBottom: 16 }}>
          <Icon name="qr" /> Aushang aktivieren
        </button>
      )}
      <div className="strong small" style={{ marginBottom: 6 }}>
        Rückmeldungen
      </div>
      {feedback.isLoading ? (
        <Spinner />
      ) : !feedback.data?.feedback.length ? (
        <div className="muted small">Noch keine.</div>
      ) : (
        <ul className="feed">
          {feedback.data.feedback.slice(0, 20).map((f) => (
            <li key={f.id}>
              <span className={`badge ${f.rating === 'schlecht' ? 'missed' : f.rating === 'gut' ? 'done' : 'warn'}`}>
                {f.rating === 'gut' ? 'sauber' : f.rating === 'schlecht' ? 'nicht sauber' : 'Meldung'}
              </span>
              <div>
                {f.topic && <div className="small">{f.topic}</div>}
                {f.text && <div>{f.text}</div>}
                <div className="muted small">
                  {formatDateTime(f.createdAt)}
                  {f.issueId ? ', als Mangel angelegt' : ''}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}

// ---------------------------------------------------------------- Youtilly-Positionen

/** "Baujahr: 1972" per line into label and value; lines without a colon keep an empty value. */
function parseAttributes(text: string): { label: string; value: string }[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 30)
    .map((line) => {
      const at = line.indexOf(':')
      return at < 0 ? { label: line.slice(0, 60), value: '' } : { label: line.slice(0, at).trim().slice(0, 60), value: line.slice(at + 1).trim().slice(0, 300) }
    })
    .filter((a) => a.label)
}

/**
 * Which Leistung is billed under which Youtilly ID here. LEG keeps one ID per
 * Gebäude and Leistung; a task on this object carries it and reports it back.
 */
function ServiceRefsTab({ node }: { node: ObjectDetail }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const refs = useQuery({
    queryKey: ['service-refs', node.id],
    queryFn: () => api<{ templateId: string; templateName: string; externalRef: string }[]>(`/api/objects/${node.id}/service-refs`),
  })
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api<{ id: string; name: string }[]>('/api/templates') })
  const [draft, setDraft] = useState<Record<string, string>>({})

  const save = useMutation({
    mutationFn: ({ templateId, externalRef }: { templateId: string; externalRef: string }) =>
      api<{ tasksUpdated: number }>(`/api/objects/${node.id}/service-refs`, { method: 'PUT', body: { templateId, externalRef: externalRef.trim() || null } }),
    onSuccess: async (result, vars) => {
      toast(vars.externalRef.trim() ? `Gespeichert${result.tasksUpdated > 0 ? `, ${result.tasksUpdated} offene Aufgaben übernehmen die ID` : ''}.` : 'ID entfernt.')
      setDraft((d) => {
        const next = { ...d }
        delete next[vars.templateId]
        return next
      })
      await queryClient.invalidateQueries({ queryKey: ['service-refs', node.id] })
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.', 'error'),
  })

  if (refs.isLoading || templates.isLoading) return <Spinner />
  const current = new Map((refs.data ?? []).map((r) => [r.templateId, r.externalRef]))

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Youtilly-Positionen</h2>
          <div className="muted small">
            Pro Leistung die Positions-ID, unter der der Kunde abrechnet. Aufgaben hier übernehmen sie
            {node.kind === 'site' ? ', Aufgaben an der Liegenschaft ohne eigene ID melden die IDs aller Gebäude darunter' : ''}. Für viele Objekte auf
            einmal: oben „Youtilly-Positionen“ per Excel.
          </div>
        </div>
      </div>
      <div className="table-scroll">
        <table className="data">
          <thead>
            <tr>
              <th>Leistung</th>
              <th>Positions-ID</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(templates.data ?? []).map((t) => {
              const saved = current.get(t.id) ?? ''
              const value = draft[t.id] ?? saved
              const dirty = value.trim() !== saved
              return (
                <tr key={t.id}>
                  <td className="strong">{t.name}</td>
                  <td>
                    <input
                      className="inline-input"
                      value={value}
                      placeholder="keine"
                      aria-label={`Positions-ID für ${t.name}`}
                      onChange={(e) => setDraft({ ...draft, [t.id]: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && dirty) save.mutate({ templateId: t.id, externalRef: value })
                      }}
                    />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {dirty && (
                      <button type="button" className="btn small" disabled={save.isPending} onClick={() => save.mutate({ templateId: t.id, externalRef: value })}>
                        Speichern
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
